import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle, ArrowUpRight, BarChart3, Bell, Flame, KeyRound, LogOut, Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LeadDetail from './LeadDetail';
import PipelineAnalytics from './PipelineAnalytics';
import CaseStudiesManager from './CaseStudiesManager';
import DiscQuizLeadsManager from './DiscQuizLeadsManager';
import { ChangePasswordModal } from '../components/admin/shared';
import { PromoPauseBanner } from '../components/admin/PromoPauseBanner';
import { AdminDashboardContext } from '../components/admin/AdminDashboardContext';
import { API, getAuthHeaders } from '../utils/admin-helpers';
import OverviewTab from '../components/admin/tabs/OverviewTab';
import LeadsTab from '../components/admin/tabs/LeadsTab';
import AssessmentsTab from '../components/admin/tabs/AssessmentsTab';
import FunnelTab from '../components/admin/tabs/FunnelTab';

const POLL_INTERVAL = 20000; // 20 seconds

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPages, setLeadsPages] = useState(1);
  const [search, setSearch] = useState('');
  const [readFilter, setReadFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [unreadCount, setUnreadCount] = useState(0);
  const [hotCount, setHotCount] = useState(0);
  const [staleCount, setStaleCount] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [scoreFilter, setScoreFilter] = useState('');
  const [team, setTeam] = useState([]);
  const [followUpMetrics, setFollowUpMetrics] = useState(null);
  const [priorityQueue, setPriorityQueue] = useState(null);
  const [revenueOnly, setRevenueOnly] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsStats, setAssessmentsStats] = useState(null);
  const [assessmentsPage, setAssessmentsPage] = useState(1);
  const [assessmentsPages, setAssessmentsPages] = useState(1);
  const [assessmentsTotal, setAssessmentsTotal] = useState(0);
  const [assessmentIndustryFilter, setAssessmentIndustryFilter] = useState('');
  const [assessmentTagFilter, setAssessmentTagFilter] = useState('');
  const [assessmentTeamSizeFilter, setAssessmentTeamSizeFilter] = useState('');
  const [assessmentNextActionFilter, setAssessmentNextActionFilter] = useState('');
  const [assessmentSortBy, setAssessmentSortBy] = useState('priority');
  const [topPriorityLeads, setTopPriorityLeads] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);
  const [assessmentDetail, setAssessmentDetail] = useState(null);
  const [nextActionOptions, setNextActionOptions] = useState([]);
  const [funnelStats, setFunnelStats] = useState(null);
  const prevUnreadRef = useRef(0);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/dashboard`, { headers: getAuthHeaders() });
      setDashboard(data);
      setUnreadCount(data.unread_leads || 0);
      setHotCount(data.hot_leads_count || 0);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  }, [navigate]);

  const fetchLeads = useCallback(async (page = 1) => {
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (readFilter !== 'all') params.read_filter = readFilter;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (assignedFilter) params.assigned_filter = assignedFilter;
      if (sortBy && sortBy !== 'newest') params.sort_by = sortBy;
      if (scoreFilter) params.score_filter = scoreFilter;
      const { data } = await axios.get(`${API}/api/admin/leads`, { headers: getAuthHeaders(), params });
      setLeads(data.leads);
      setLeadsTotal(data.total);
      setLeadsPage(data.page);
      setLeadsPages(data.pages);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  }, [search, readFilter, statusFilter, assignedFilter, sortBy, scoreFilter, navigate]);

  const pollNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/notifications`, { headers: getAuthHeaders() });
      setHotCount(data.hot_leads_count || 0);
      setStaleCount(data.stale_leads || 0);
      setUnreadCount(prev => {
        if (data.unread_count > prev && prev > 0) {
          toast.info(`${data.unread_count - prev} new lead${data.unread_count - prev > 1 ? 's' : ''} received!`, { duration: 5000 });
          // Refresh dashboard + leads data
          fetchDashboard();
          fetchLeads(leadsPage);
        }
        return data.unread_count;
      });
    } catch (err) {
      console.error('[pollNotifications] failed:', err?.message);
    }
  }, [fetchDashboard, fetchLeads, leadsPage]);

  const fetchAssessments = useCallback(async (page = 1) => {
    try {
      const params = { page, limit: 15 };
      if (assessmentIndustryFilter) params.industry = assessmentIndustryFilter;
      if (assessmentTagFilter) params.lead_tag = assessmentTagFilter;
      if (assessmentTeamSizeFilter) params.team_size = assessmentTeamSizeFilter;
      if (assessmentNextActionFilter) params.next_action = assessmentNextActionFilter;
      if (assessmentSortBy) params.sort_by = assessmentSortBy;
      const { data } = await axios.get(`${API}/api/admin/assessments`, { headers: getAuthHeaders(), params });
      setAssessments(data.assessments);
      setAssessmentsTotal(data.total);
      setAssessmentsPage(data.page);
      setAssessmentsPages(data.pages);
      setAssessmentsStats(data.stats);
      setTopPriorityLeads(data.top_priority || []);
      setNextActionOptions(data.next_action_options || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    }
  }, [assessmentIndustryFilter, assessmentTagFilter, assessmentTeamSizeFilter, assessmentNextActionFilter, assessmentSortBy, navigate]);

  const fetchAssessmentDetail = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/api/admin/assessments/${id}`, { headers: getAuthHeaders() });
      setAssessmentDetail(data);
      setSelectedAssessmentId(id);
    } catch (err) {
      toast.error('Failed to load assessment details');
    }
  }, []);

  const updateEngagement = async (assessmentId, action) => {
    try {
      await axios.post(`${API}/api/admin/assessments/${assessmentId}/engagement?action=${action}`, {}, { headers: getAuthHeaders() });
      toast.success(`Engagement updated: ${action.replace('_', ' ')}`);
      fetchAssessments(assessmentsPage);
      if (selectedAssessmentId === assessmentId) {
        fetchAssessmentDetail(assessmentId);
      }
    } catch (err) {
      toast.error('Failed to update engagement');
    }
  };

  const updatePipeline = async (assessmentId, nextAction, markContacted = false) => {
    try {
      await axios.patch(`${API}/api/admin/assessments/${assessmentId}/pipeline`, {
        next_action: nextAction,
        mark_contacted: markContacted
      }, { headers: getAuthHeaders() });
      toast.success(markContacted ? 'Marked as contacted' : `Status: ${nextAction}`);
      fetchAssessments(assessmentsPage);
      if (selectedAssessmentId === assessmentId) {
        fetchAssessmentDetail(assessmentId);
      }
    } catch (err) {
      toast.error('Failed to update pipeline');
    }
  };

  const fetchFunnelStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/funnel-dashboard`, { headers: getAuthHeaders() });
      setFunnelStats(data);
    } catch (err) {
      console.error('Failed to fetch funnel stats:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    Promise.all([fetchDashboard(), fetchLeads()]).then(() => setLoading(false));
    axios.get(`${API}/api/admin/team`, { headers: getAuthHeaders() }).then(r => setTeam(r.data)).catch(() => {});
    axios.get(`${API}/api/admin/follow-up-metrics`, { headers: getAuthHeaders() }).then(r => setFollowUpMetrics(r.data)).catch(() => {});
    axios.get(`${API}/api/admin/priority-queue?limit=10`, { headers: getAuthHeaders() }).then(r => setPriorityQueue(r.data)).catch(() => {});
    // Fetch assessments
    fetchAssessments();
    // Fetch funnel stats
    fetchFunnelStats();
    // Fetch stale count immediately
    axios.get(`${API}/api/admin/notifications`, { headers: getAuthHeaders() }).then(r => {
      setStaleCount(r.data.stale_leads || 0);
      setHotCount(r.data.hot_leads_count || 0);
    }).catch(() => {});
  }, [fetchDashboard, fetchLeads, fetchAssessments, fetchFunnelStats, navigate]);

  // Polling interval
  useEffect(() => {
    const interval = setInterval(pollNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [pollNotifications]);

  // Track previous unread for toast
  useEffect(() => {
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  useEffect(() => {
    fetchLeads(1);
  }, [search, readFilter, statusFilter, assignedFilter, sortBy, scoreFilter, fetchLeads]);

  // Refetch priority queue when revenue toggle changes
  useEffect(() => {
    axios.get(`${API}/api/admin/priority-queue?limit=10&revenue_only=${revenueOnly}`, { headers: getAuthHeaders() })
      .then(r => setPriorityQueue(r.data)).catch(() => {});
  }, [revenueOnly]);

  // Refetch assessments when filters change
  useEffect(() => {
    fetchAssessments(1);
  }, [assessmentIndustryFilter, assessmentTagFilter, assessmentTeamSizeFilter, assessmentNextActionFilter, assessmentSortBy, fetchAssessments]);

  const toggleRead = async (id, isRead) => {
    try {
      const endpoint = isRead ? 'unread' : 'read';
      await axios.patch(`${API}/api/admin/leads/${id}/${endpoint}`, {}, { headers: getAuthHeaders() });
      fetchLeads(leadsPage);
      fetchDashboard();
    } catch (err) {
      console.error('[toggleRead] failed:', err?.message);
      toast.error('Failed to update lead');
    }
  };

  const exportCSV = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/leads/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('[exportCSV] failed:', err?.message);
      toast.error('CSV export failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const sendMorningBriefing = async () => {
    try {
      await axios.post(`${API}/api/admin/morning-briefing/send`, {}, { headers: getAuthHeaders() });
      toast.success('Morning briefing sent to brandon@thewementality.org');
    } catch (err) {
      toast.error('Failed to send briefing. Check SendGrid configuration.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  const d = dashboard || {};

  const ctx = { dashboard, setDashboard, leads, setLeads, leadsTotal, setLeadsTotal, leadsPage, setLeadsPage, leadsPages, setLeadsPages, search, setSearch, readFilter, setReadFilter, loading, setLoading, activeTab, setActiveTab, unreadCount, setUnreadCount, hotCount, setHotCount, staleCount, setStaleCount, showPasswordModal, setShowPasswordModal, selectedLeadId, setSelectedLeadId, statusFilter, setStatusFilter, assignedFilter, setAssignedFilter, sortBy, setSortBy, scoreFilter, setScoreFilter, team, setTeam, followUpMetrics, setFollowUpMetrics, priorityQueue, setPriorityQueue, revenueOnly, setRevenueOnly, assessments, setAssessments, assessmentsStats, setAssessmentsStats, assessmentsPage, setAssessmentsPage, assessmentsPages, setAssessmentsPages, assessmentsTotal, setAssessmentsTotal, assessmentIndustryFilter, setAssessmentIndustryFilter, assessmentTagFilter, setAssessmentTagFilter, assessmentTeamSizeFilter, setAssessmentTeamSizeFilter, assessmentNextActionFilter, setAssessmentNextActionFilter, assessmentSortBy, setAssessmentSortBy, topPriorityLeads, setTopPriorityLeads, selectedAssessmentId, setSelectedAssessmentId, assessmentDetail, setAssessmentDetail, nextActionOptions, setNextActionOptions, funnelStats, setFunnelStats, fetchDashboard, fetchLeads, fetchAssessments, fetchAssessmentDetail, fetchFunnelStats, updateEngagement, updatePipeline, toggleRead, exportCSV, navigate, d };

  return (
    <div className="min-h-screen bg-slate-950">
      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />}
      {selectedLeadId && (
        <LeadDetail
          leadId={selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
          onUpdate={() => { fetchLeads(leadsPage); fetchDashboard(); axios.get(`${API}/api/admin/follow-up-metrics`, { headers: getAuthHeaders() }).then(r => setFollowUpMetrics(r.data)).catch(() => {}); }}
        />
      )}

      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-cyan-400" />
            <h1 className="text-white font-bold text-lg" data-testid="dashboard-title">Admin Dashboard</h1>
            {/* Notification Badge */}
            {unreadCount > 0 && (
              <button
                onClick={() => { setActiveTab('leads'); setReadFilter('unread'); }}
                className="relative flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
                data-testid="notification-badge"
              >
                <Bell className="h-3.5 w-3.5 text-cyan-400" />
                <span className="text-cyan-400 text-xs font-bold">{unreadCount}</span>
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-cyan-400" />
              </button>
            )}
            {hotCount > 0 && (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-400 text-xs font-bold" data-testid="hot-leads-badge">
                <Flame className="h-3.5 w-3.5" /> {hotCount} hot
              </span>
            )}
            {staleCount > 0 && (
              <button
                onClick={() => { setActiveTab('leads'); setStatusFilter('stale'); }}
                className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold hover:bg-red-500/25 transition-colors"
                data-testid="stale-leads-header-badge"
              >
                <AlertTriangle className="h-3.5 w-3.5" /> {staleCount} stale
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button onClick={sendMorningBriefing} variant="ghost" size="sm" className="text-slate-400 hover:text-cyan-400 hidden sm:flex" data-testid="send-briefing-button" title="Send Morning Briefing Email">
              <Mail className="h-4 w-4 sm:mr-2" /><span className="hidden lg:inline">Briefing</span>
            </Button>
            <a href="/" className="text-slate-400 hover:text-white text-sm hidden sm:block" data-testid="view-site-link">
              <ArrowUpRight className="h-4 w-4 inline mr-1" /> View Site
            </a>
            <Button onClick={() => setShowPasswordModal(true)} variant="ghost" size="sm" className="text-slate-400 hover:text-white hidden sm:flex" data-testid="change-password-button">
              <KeyRound className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Password</span>
            </Button>
            <Button onClick={logout} variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="logout-button">
              <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <AdminDashboardContext.Provider value={ctx}>
        <PromoPauseBanner />
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-900 rounded-lg p-1 w-fit" data-testid="dashboard-tabs">
          {['overview', 'leads', 'assessments', 'funnel', 'analytics', 'case-studies', 'disc-quiz'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
              className={`relative px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
                activeTab === tab ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab === 'case-studies' ? 'Case Studies' : tab === 'disc-quiz' ? 'DISC Quiz' : tab}
              {tab === 'leads' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-cyan-500 text-white text-[10px] font-bold px-1" data-testid="leads-tab-badge">
                  {unreadCount}
                </span>
              )}
              {tab === 'assessments' && assessmentsStats?.urgent > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="assessments-tab-badge">
                  {assessmentsStats.urgent}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <OverviewTab />}

        {activeTab === 'leads' && <LeadsTab />}

        {activeTab === 'assessments' && <AssessmentsTab />}

        {activeTab === 'funnel' && <FunnelTab />}

        {activeTab === 'analytics' && (
          <PipelineAnalytics />
        )}

        {activeTab === 'case-studies' && (
          <CaseStudiesManager />
        )}

        {activeTab === 'disc-quiz' && (
          <DiscQuizLeadsManager />
        )}
              </AdminDashboardContext.Provider>
      </main>
    </div>
  );
}
